import type { ITuningClock, ITuningTelemetrySource } from '../contracts/ports';
import type { TuningIdentity } from '../contracts/identity';
import { tuningHistogramBucketIndex } from '../contracts/measurements';
import type { TuningHistogram, TuningMetric, TuningTelemetryWindow } from '../contracts/measurements';
import type { HostStatsSample, IHostStatsFeed, IHostTelemetryContextSource } from './HostTelemetryContracts';

type Stat = {
  id: string; type: string; kind?: string; mediaType?: string; isRemote?: boolean;
  mediaSourceId?: string; remoteId?: string; codecId?: string; transportId?: string;
  selectedCandidatePairId?: string; state?: string; nominated?: boolean; ssrc?: number;
  framesPerSecond?: number; currentRoundTripTime?: number; frameWidth?: number; frameHeight?: number;
  localCandidateId?: string; remoteCandidateId?: string; candidateType?: string;
  mimeType?: string; encoderImplementation?: string; [key: string]: unknown;
};
type Baseline = { at: number; outbound: Stat; source?: Stat; remote?: Stat; epoch: string };
const metric = (value: number | undefined, unit: TuningMetric['unit'], source: string,
  semantics: TuningMetric['semantics'] = 'gauge'): TuningMetric => ({
  value: value === undefined ? null : value, unit, source, semantics,
  availability: value === undefined ? 'missing' : 'measured', sampleCount: value === undefined ? 0 : 1,
});
const numeric = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? value : undefined;
const delta = (current: Stat | undefined, previous: Stat | undefined, key: string): number | undefined => {
  const next = numeric(current?.[key]); const before = numeric(previous?.[key]);
  return next !== undefined && before !== undefined && next >= before ? next - before : undefined;
};

/** Native source/encode/network counters retain separate units and reset boundaries. */
export class HostTelemetrySource implements ITuningTelemetrySource {
  private readonly listeners = new Set<(window: TuningTelemetryWindow) => void>();
  private readonly unsubscribe: () => void;
  private target: TuningIdentity | undefined;
  private baseline: Baseline | undefined;
  private interval = 1_000;
  private sequence = 0;
  private rotation = 0;
  private lastPairId: string | undefined;
  private pathEpoch = 0;
  private readonly now: ITuningClock;
  private timing: { epoch: string; startMs: number; endMs: number; counts: Map<number, number>; count: number; maxMs: number } | undefined;
  private timingDropped = 0;

  constructor(private readonly options: { feed: IHostStatsFeed; clock: ITuningClock; context: IHostTelemetryContextSource }) {
    this.now = options.clock;
    this.unsubscribe = options.feed.subscribe(sample => this.observe(sample));
  }

  configure(target: TuningIdentity | undefined, windowMs: 250 | 500 | 1000 = 1_000): void {
    if (!target || target.trialId !== this.target?.trialId || target.connectionEpoch !== this.target?.connectionEpoch) {
      this.baseline = undefined; this.lastPairId = undefined; this.pathEpoch = 0;
      this.timing = undefined;
    }
    this.target = target && { ...target }; this.interval = windowMs;
  }
  subscribe(listener: (window: TuningTelemetryWindow) => void): () => void {
    this.listeners.add(listener); return () => this.listeners.delete(listener);
  }
  dispose(): void { this.unsubscribe(); this.listeners.clear(); this.target = undefined; this.baseline = undefined; }

  /** Starts at the host encoded-frame send timestamp, never at capture or physical display scanout. */
  observeTiming(sample: { target: TuningIdentity; encodeToCompositionMs: number; receivedMonotonicMs: number;
    pathEpoch: number; timingSource: 'presentation-time' | 'callback-time' }): void {
    if (sample.timingSource !== 'presentation-time' || !this.target || sample.target.sessionId !== this.target.sessionId || sample.target.peerId !== this.target.peerId
      || sample.target.trialId !== this.target.trialId || sample.target.connectionEpoch !== this.target.connectionEpoch
      || sample.target.configRevision !== this.target.configRevision || !Number.isFinite(sample.encodeToCompositionMs)
      || sample.encodeToCompositionMs < 0 || !Number.isFinite(sample.receivedMonotonicMs) || sample.receivedMonotonicMs < 0) return;
    const context = this.options.context.snapshot(sample.target);
    if (context.clock?.state !== 'calibrated' || sample.pathEpoch !== context.pathEpoch) return;
    const epoch = `${sample.target.trialId}/${sample.target.connectionEpoch}/${sample.target.configRevision}/${sample.pathEpoch}`;
    if (this.timing && (this.timing.epoch !== epoch || sample.receivedMonotonicMs < this.timing.endMs)) {
      this.timingDropped += this.timing.count; this.timing = undefined;
    }
    this.timing ??= { epoch, startMs: sample.receivedMonotonicMs, endMs: sample.receivedMonotonicMs, counts: new Map(), count: 0, maxMs: 0 };
    if (this.timing.count >= 1_024) { this.timingDropped++; return; }
    const bucket = tuningHistogramBucketIndex(sample.encodeToCompositionMs);
    if (!this.timing.counts.has(bucket) && this.timing.counts.size >= 96) {
      const removed = [...this.timing.counts.keys()].sort((a, b) => a - b)[0], count = this.timing.counts.get(removed) ?? 0;
      this.timing.counts.delete(removed); this.timing.count -= count; this.timingDropped += count;
    }
    this.timing.counts.set(bucket, (this.timing.counts.get(bucket) ?? 0) + 1); this.timing.count++;
    this.timing.endMs = sample.receivedMonotonicMs; this.timing.maxMs = Math.max(this.timing.maxMs, sample.encodeToCompositionMs);
  }

  private observe(sample: HostStatsSample): void {
    if (!this.target || sample.target.trialId !== this.target.trialId || sample.target.connectionEpoch !== this.target.connectionEpoch) return;
    if (sample.target.sessionId !== this.target.sessionId || sample.target.peerId !== this.target.peerId) return;
    if (this.baseline && sample.receivedMonotonicMs < this.baseline.at) this.baseline = undefined;
    if (this.baseline && sample.receivedMonotonicMs - this.baseline.at < this.interval) return;
    const stats: Stat[] = [];
    sample.report.forEach(entry => stats.push(entry as unknown as Stat));
    const outbound = stats.find(entry => entry.type === 'outbound-rtp' && (entry.kind || entry.mediaType) === 'video' && !entry.isRemote);
    if (!outbound) return;
    const byId = (id: unknown) => stats.find(entry => entry.id === id);
    const source = byId(outbound.mediaSourceId);
    const remote = byId(outbound.remoteId);
    const codec = byId(outbound.codecId);
    const transport = byId(outbound.transportId);
    const pair = byId(transport?.selectedCandidatePairId) || stats.find(entry => entry.type === 'candidate-pair' && entry.state === 'succeeded' && entry.nominated);
    const epoch = `${sample.endpointConnectionEpoch}/${sample.captureGeneration}/${sample.target.configRevision}/${outbound.id}/${outbound.ssrc}/${source?.id}/${pair?.id}`;
    const previous = this.baseline?.epoch === epoch ? this.baseline : undefined;
    const elapsed = previous ? sample.receivedMonotonicMs - previous.at : 0;
    const context = this.options.context.snapshot(sample.target);
    if (this.lastPairId !== undefined && pair?.id !== this.lastPairId) this.pathEpoch++;
    this.lastPairId = pair?.id;
    const frames = delta(outbound, previous?.outbound, 'framesEncoded');
    const packets = delta(outbound, previous?.outbound, 'packetsSent');
    const bytes = delta(outbound, previous?.outbound, 'bytesSent');
    const rttSeconds = numeric(pair?.currentRoundTripTime);
    const perSecond = (value: number | undefined) => value === undefined || elapsed <= 0 ? undefined : value * 1_000 / elapsed;
    const millisecondsPer = (total: number | undefined, count: number | undefined) => total === undefined || !count ? undefined : total * 1_000 / count;
    const values: Record<string, TuningMetric> = {
      'source.fps': metric(perSecond(delta(source, previous?.source, 'frames')) ?? numeric(source?.framesPerSecond), 'fps', 'media-source'),
      'encode.fps': metric(perSecond(frames), 'fps', 'outbound-rtp.framesEncoded'),
      'send.bitrateBps': metric(perSecond(bytes) === undefined ? undefined : perSecond(bytes)! * 8, 'bps', 'outbound-rtp.bytesSent'),
      'network.rttMs': metric(rttSeconds === undefined ? undefined : rttSeconds * 1_000, 'ms', 'candidate-pair'),
      'network.packetsLost': metric(delta(remote, previous?.remote, 'packetsLost'), 'count', 'remote-inbound-rtp', 'interval-delta'),
      'network.retransmittedPackets': metric(delta(outbound, previous?.outbound, 'retransmittedPacketsSent'), 'count', 'outbound-rtp', 'interval-delta'),
      'encode.residencyMs': metric(millisecondsPer(delta(outbound, previous?.outbound, 'totalEncodeTime'), frames), 'ms', 'outbound-rtp.totalEncodeTime'),
      'send.packetResidenceMs': metric(millisecondsPer(delta(outbound, previous?.outbound, 'totalPacketSendDelay'), packets), 'ms', 'outbound-rtp.totalPacketSendDelay'),
      'encode.width': metric(numeric(outbound.frameWidth), 'count', 'outbound-rtp.frameWidth'),
      'encode.height': metric(numeric(outbound.frameHeight), 'count', 'outbound-rtp.frameHeight'),
      'encode.keyframes': metric(delta(outbound, previous?.outbound, 'keyFramesEncoded'), 'count', 'outbound-rtp', 'interval-delta'),
      'network.nack': metric(delta(outbound, previous?.outbound, 'nackCount'), 'count', 'outbound-rtp', 'interval-delta'),
      'network.pli': metric(delta(outbound, previous?.outbound, 'pliCount'), 'count', 'outbound-rtp', 'interval-delta'),
      'network.fir': metric(delta(outbound, previous?.outbound, 'firCount'), 'count', 'outbound-rtp', 'interval-delta'),
      'observer.readMs': metric(sample.readDurationMs, 'ms', 'HostStatsFeed'),
      'settings.maxBitrateBps': metric(context.effectiveBitrateBps, 'bps', 'sender-readback'),
      'settings.maxFramerate': metric(context.effectiveFps, 'fps', 'sender-readback'),
      'settings.scale': metric(context.effectiveScale, 'count', 'sender-readback'),
      'recovery.ceilingBps': metric(context.recoveryCeilingBps, 'bps', 'application-policy'),
      'recovery.floorBps': metric(context.recoveryFloorBps, 'bps', 'application-policy'),
      'keyframes.requested': metric(context.keyframesRequested, 'count', 'application', 'cumulative'),
      'keyframes.accepted': metric(context.keyframesAccepted, 'count', 'application', 'cumulative'),
      'keyframes.withheld': metric(context.keyframesWithheld, 'count', 'application', 'cumulative'),
      'process.rendererCpuPercent': metric(context.rendererCpuPercent, 'percent', 'electron-app-metrics'),
      'observer.timingSamplesDropped': metric(this.timingDropped, 'count', 'HostTelemetrySource', 'cumulative'),
    };
    const local = byId(pair?.localCandidateId); const remoteCandidate = byId(pair?.remoteCandidateId);
    const route = !local || !remoteCandidate ? 'unknown' : local.candidateType === 'relay' || remoteCandidate.candidateType === 'relay' ? 'relay' : 'direct';
    const window: TuningTelemetryWindow = { version: 1, target: { ...sample.target }, endpoint: 'host', sequence: ++this.sequence,
      monotonicStartMs: previous?.at ?? sample.receivedMonotonicMs, monotonicEndMs: sample.receivedMonotonicMs,
      wallAnchorMs: sample.wallAnchorMs ?? this.now.wallMs(), clock: context.clock || { state: 'uncalibrated' },
      pathEpoch: Math.max(context.pathEpoch, this.pathEpoch), captureGeneration: sample.captureGeneration,
      sourceId: context.sourceId || source?.id, endpointConnectionEpoch: sample.endpointConnectionEpoch,
      metrics: {}, distributions: {}, telemetryDropped: 0, sampleSkipped: sample.skippedReads, visibility: context.visibility || 'unknown',
      ...(this.sequence % 4 === 1 ? { context: { ...context.labels, ...(codec?.mimeType ? { codec: codec.mimeType } : {}),
        ...(outbound.encoderImplementation ? { encoderImplementation: outbound.encoderImplementation } : {}), route } } : {}) };
    const timingEpoch = `${sample.target.trialId}/${sample.target.connectionEpoch}/${sample.target.configRevision}/${context.pathEpoch}`;
    if (this.timing && (!previous || context.clock?.state !== 'calibrated' || this.timing.epoch !== timingEpoch
      || this.timing.startMs < previous.at || this.timing.endMs > sample.receivedMonotonicMs)) {
      this.timingDropped += this.timing.count; this.timing = undefined;
    }
    if (this.timing && previous && sample.receivedMonotonicMs > previous.at) {
      const timing = this.timing;
      const histogram: TuningHistogram = { schema: 'ms-v1', unit: 'ms', source: 'host-encoded-frame-to-guest-composition', availability: 'measured',
        monotonicStartMs: previous.at, monotonicEndMs: sample.receivedMonotonicMs,
        buckets: [...timing.counts].sort(([a], [b]) => a - b), sampleCount: timing.count, maxMs: timing.maxMs };
      (window.distributions as Record<string, TuningHistogram>)['encodeToCompositionMs'] = histogram;
      this.timing = undefined;
    }
    const metrics = window.metrics as Record<string, TuningMetric>;
    const entries = Object.entries(values);
    for (const [key, value] of entries.slice(0, 6)) metrics[key] = value;
    const optional = entries.slice(6);
    for (let i = 0; i < optional.length; i++) {
      const [key, value] = optional[(this.rotation + i) % optional.length];
      metrics[key] = value;
      if (new TextEncoder().encode(JSON.stringify(window)).length > 1_900) { delete metrics[key]; this.rotation = (this.rotation + Math.max(1, i)) % optional.length; break; }
    }
    this.baseline = { at: sample.receivedMonotonicMs, outbound: { ...outbound }, source: source && { ...source }, remote: remote && { ...remote }, epoch };
    for (const listener of this.listeners) listener(window);
  }
}
